/**
 * The employee's business card (FRD-HRP-079, CLR-65): the address its code opens, and the contact
 * card a phone saves from the card page. Shared by the employee record and the public card page.
 */

/** The public card page a business-card code opens: anyone holding it can read it signed out. */
export function businessCardUrl(token: string, origin = window.location.origin): string {
  return `${origin}/card/${token}`;
}

/** A value the API masked: a mask is not a value, so it never goes on a card. */
const masked = (value: string | null) => value?.startsWith('••••') ?? false;
const usable = (value: string | null) => (value === null || masked(value) ? null : value);

/** vCard 3.0 text values escape the backslash, comma, semicolon and line break (RFC 2426 §4). */
const escaped = (value: string) =>
  value
    .replace(/\\/g, '\\\\')
    .replace(/([,;])/g, '\\$1')
    .replace(/\r?\n/g, '\\n');

/**
 * The employee's contact card (vCard 3.0): name, position, the company, the work e-mail and
 * telephone, and the photo where one is given (CLR-65), base64 JPEG. A masked value is left out,
 * and nothing sensitive is included.
 */
export function businessCard(
  employee: { name: string; contactEmail: string | null; contactPhone: string | null },
  position: string | null,
  organization: string | null,
  photo?: string,
): string {
  const email = usable(employee.contactEmail);
  const phone = usable(employee.contactPhone);
  return [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${escaped(employee.name)}`,
    `N:${escaped(employee.name)};;;;`,
    ...(organization === null ? [] : [`ORG:${escaped(organization)}`]),
    ...(position === null ? [] : [`TITLE:${escaped(position)}`]),
    ...(email === null ? [] : [`EMAIL;TYPE=WORK:${escaped(email)}`]),
    ...(phone === null ? [] : [`TEL;TYPE=WORK:${escaped(phone)}`]),
    ...(photo === undefined ? [] : [`PHOTO;ENCODING=b;TYPE=JPEG:${photo}`]),
    'END:VCARD',
  ].join('\r\n');
}

/** The work e-mail and telephone a card shows: masked values are left out. */
export function cardContact(card: { contactEmail: string | null; contactPhone: string | null }) {
  return { email: usable(card.contactEmail), phone: usable(card.contactPhone) };
}

/** Thumbnail sides in pixels and JPEG qualities, tried largest and best first. */
const PHOTO_SIDES = [256, 192, 128];
const PHOTO_QUALITIES = [0.85, 0.7, 0.5];
/**
 * The longest photo, in base64 characters, a saved contact carries: about 60 KB, well inside what
 * the phones' contact apps accept, and plenty for a sharp contact picture.
 */
export const CONTACT_PHOTO_LIMIT = 80_000;

/**
 * The employee's picture as a square JPEG for the contact's PHOTO (CLR-65), base64 without the
 * data: prefix: the largest that fits {@link CONTACT_PHOTO_LIMIT}, cut to the centred square as
 * the picture is shown on the record. Undefined when the picture cannot be loaded or no size fits.
 * The picture is same-origin, so the canvas stays readable.
 */
export async function contactPhoto(src: string): Promise<string | undefined> {
  const image = new Image();
  image.src = src;
  try {
    await image.decode();
  } catch {
    return undefined;
  }
  const crop = Math.min(image.naturalWidth, image.naturalHeight);
  for (const side of PHOTO_SIDES) {
    const canvas = document.createElement('canvas');
    canvas.width = side;
    canvas.height = side;
    const context = canvas.getContext('2d');
    if (context === null) return undefined;
    // JPEG has no transparency: a transparent picture is laid on white, not black.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, side, side);
    context.drawImage(
      image,
      (image.naturalWidth - crop) / 2,
      (image.naturalHeight - crop) / 2,
      crop,
      crop,
      0,
      0,
      side,
      side,
    );
    for (const quality of PHOTO_QUALITIES) {
      const url = canvas.toDataURL('image/jpeg', quality);
      const data = url.slice(url.indexOf(',') + 1);
      if (url.startsWith('data:image/jpeg;base64,') && data.length <= CONTACT_PHOTO_LIMIT) {
        return data;
      }
    }
  }
  return undefined;
}

/** The employee's name kept to letters, digits and dashes, for a file name. */
function slug(name: string): string {
  const kept = name
    .normalize('NFKD')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
  return kept === '' ? 'employee' : kept;
}

/** The downloaded code image's file name. */
export function businessCardFileName(name: string): string {
  return `${slug(name)}-business-card-qr.png`;
}

/** The saved contact's file name. */
export function contactFileName(name: string): string {
  return `${slug(name)}.vcf`;
}
